import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Clock, ExternalLink, Loader2, X as XIcon } from "lucide-react";
import { api } from "../lib/api";
import type { PacketSourceResolved } from "../lib/api";
import { PacketDetailRouter } from "./PacketDetailRouter";

/** Q42 universal packet detail drawer.
 *
 *  Slides in from the right (full-screen modal on narrow viewports).
 *  Fetches /api/packets/:id/source, dispatches to the matching
 *  per-source renderer, and exposes inline actions (open in
 *  original, snooze, draft reply, archive) on the connector-backed
 *  kinds.
 *
 *  Read-state preservation runs at the backend connector layer —
 *  this drawer never asks for a "mark read" side effect. */

const SNOOZE_OPTIONS: { label: string; hours: number }[] = [
  { label: "1 hour", hours: 1 },
  { label: "Tomorrow", hours: 24 },
  { label: "3 days", hours: 24 * 3 },
  { label: "1 week", hours: 24 * 7 },
];

export function PacketDetailDrawer({ packetId, onClose }: { packetId: string; onClose: () => void }) {
  const [resolved, setResolved] = useState<PacketSourceResolved | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [snoozeBusy, setSnoozeBusy] = useState(false);
  const [snoozeMenuOpen, setSnoozeMenuOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    setResolved(null);
    setError(null);
    api
      .packetSource(packetId)
      .then((d) => {
        if (alive) setResolved(d);
      })
      .catch((e: Error) => {
        if (alive) setError(e.message);
      });
    return () => {
      alive = false;
    };
  }, [packetId]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const onSnooze = async (hours: number) => {
    if (!resolved) return;
    setSnoozeBusy(true);
    setSnoozeMenuOpen(false);
    try {
      const r = await api.snoozePacket(packetId, { hours });
      setResolved({ ...resolved, snooze: { snoozed: true, until_at: r.until_at } });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSnoozeBusy(false);
    }
  };
  const onUnsnooze = async () => {
    if (!resolved) return;
    setSnoozeBusy(true);
    try {
      await api.unsnoozePacket(packetId);
      setResolved({ ...resolved, snooze: { snoozed: false, until_at: null } });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSnoozeBusy(false);
    }
  };

  const title = useMemo(() => {
    if (!resolved) return "Source";
    const h = resolved.render.header;
    return h.subject || h.threadTopic || h.channelName || "Source";
  }, [resolved]);

  return createPortal(
    <div
      className="fixed inset-0 z-50 bg-slate-950/40 dark:bg-slate-950/70 backdrop-blur-sm flex justify-end"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Source packet detail"
    >
      <div
        className="w-full sm:max-w-2xl bg-white dark:bg-slate-900 sm:border-l border-slate-200 dark:border-slate-700 shadow-2xl overflow-y-auto flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sticky top-0 z-10 flex items-center justify-between gap-3 px-4 sm:px-6 py-3 border-b border-slate-200 dark:border-slate-700 bg-white/95 dark:bg-slate-900/95 backdrop-blur">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
              {resolved ? labelForKind(resolved.render.kind) : "Loading"}
              {resolved?.fetch.status === "cached" && (
                <span className="ml-2 normal-case font-normal text-slate-400">cached snapshot</span>
              )}
              {resolved?.fetch.status === "stub" && (
                <span className="ml-2 normal-case font-normal text-amber-600 dark:text-amber-300">
                  connector offline
                </span>
              )}
            </div>
            <h2 className="text-base font-semibold text-slate-800 dark:text-slate-100 truncate">
              {title}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 flex-shrink-0"
          >
            <XIcon className="w-4 h-4" />
          </button>
        </header>

        {error && (
          <div className="px-4 sm:px-6 py-3 text-sm text-rose-600 dark:text-rose-300">
            {error}
          </div>
        )}

        {!resolved && !error && (
          <div className="px-4 sm:px-6 py-10 flex items-center justify-center text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        )}

        {resolved && (
          <>
            {!resolved.connector.connected && resolved.connector.reauthHint && (
              <ConnectorOfflineBanner hint={resolved.connector.reauthHint} />
            )}
            {resolved.snooze.snoozed && resolved.snooze.until_at && (
              <SnoozeBanner until={resolved.snooze.until_at} onUnsnooze={onUnsnooze} busy={snoozeBusy} />
            )}
            <div className="flex-1">
              <PacketDetailRouter resolved={resolved} />
            </div>
            <footer className="sticky bottom-0 z-10 flex flex-wrap items-center gap-2 px-4 sm:px-6 py-3 border-t border-slate-200 dark:border-slate-700 bg-white/95 dark:bg-slate-900/95 backdrop-blur">
              {resolved.actions.canOpenExternal && resolved.actions.externalUrl && (
                <a
                  href={resolved.actions.externalUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-sm font-semibold px-3 py-1.5 hover:opacity-90"
                >
                  Open in {resolved.connector.displayName}
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
              <div className="relative">
                <button
                  type="button"
                  disabled={snoozeBusy}
                  onClick={() => setSnoozeMenuOpen((v) => !v)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-sm font-medium px-3 py-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50"
                >
                  <Clock className="w-3.5 h-3.5" />
                  Snooze
                </button>
                {snoozeMenuOpen && (
                  <div className="absolute bottom-full mb-2 left-0 z-20 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl min-w-[10rem] py-1">
                    {SNOOZE_OPTIONS.map((o) => (
                      <button
                        key={o.label}
                        type="button"
                        onClick={() => onSnooze(o.hours)}
                        className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-100 dark:hover:bg-slate-800"
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="ml-auto text-[11px] text-slate-400 dark:text-slate-500 truncate">
                {resolved.packet.source_kind}
                {resolved.packet.direction ? ` · ${resolved.packet.direction}` : ""}
              </div>
            </footer>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

function ConnectorOfflineBanner({ hint }: { hint: string }) {
  return (
    <div className="mx-4 sm:mx-6 my-3 rounded-lg border border-amber-200 dark:border-amber-700/50 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
      {hint}
    </div>
  );
}

function SnoozeBanner({
  until,
  onUnsnooze,
  busy,
}: {
  until: string;
  onUnsnooze: () => void;
  busy: boolean;
}) {
  return (
    <div className="mx-4 sm:mx-6 my-3 rounded-lg border border-sky-200 dark:border-sky-700/50 bg-sky-50 dark:bg-sky-900/20 px-3 py-2 text-xs text-sky-900 dark:text-sky-200 flex items-center justify-between">
      <span>
        Snoozed until {new Date(until).toLocaleString()}.
      </span>
      <button
        type="button"
        onClick={onUnsnooze}
        disabled={busy}
        className="font-semibold underline disabled:opacity-50"
      >
        Unsnooze
      </button>
    </div>
  );
}

function labelForKind(k: string): string {
  switch (k) {
    case "outlook-email": return "Outlook email";
    case "outlook-calendar": return "Outlook event";
    case "outlook-task": return "Outlook task";
    case "gmail-email": return "Gmail";
    case "calendar-event": return "Calendar event";
    case "teams-message": return "Teams message";
    case "whatsapp-message": return "WhatsApp";
    case "sms-message": return "SMS";
    case "obsidian-note": return "Obsidian note";
    case "voice-transcript": return "Voice capture";
    case "rss-item": return "RSS";
    case "linkedin": return "LinkedIn";
    case "manual-note": return "Capture";
    default: return "Source";
  }
}
